import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Dumbbell } from "lucide-react";

/**
 * Flip-card success animation.
 * Front: green check in circle. Back: dumbbell.
 * Cycles N times then stays on check.
 */
const SuccessAnimation = ({ message, cycles = 3 }) => {
  const [showCheck, setShowCheck] = useState(true);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let active = true;
    let count = 0;
    const interval = setInterval(() => {
      if (!active) return;
      count += 1;
      setShowCheck((s) => !s);
      if (count >= cycles * 2) {
        clearInterval(interval);
        // Ensure ending on check
        setTimeout(() => active && setShowCheck(true), 50);
        setTimeout(() => active && setDone(true), 500);
      }
    }, 950);
    return () => { active = false; clearInterval(interval); };
  }, [cycles]);

  return (
    <div className="flex flex-col items-center justify-center py-8 sm:py-10 text-center">
      <div
        className="relative w-24 h-24 sm:w-28 sm:h-28"
        style={{ perspective: "1000px" }}
        data-testid="success-animation"
      >
        <motion.div
          animate={{ rotateY: showCheck ? 0 : 180 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="relative w-full h-full"
          style={{ transformStyle: "preserve-3d" }}
        >
          {/* Front - check */}
          <div
            className="absolute inset-0 rounded-full flex items-center justify-center"
            style={{
              backfaceVisibility: "hidden",
              background: "radial-gradient(circle at 30% 30%, #34d399, #059669 70%)",
              boxShadow: "0 12px 40px -8px rgba(16, 185, 129, 0.55), inset 0 1px 0 rgba(255,255,255,0.4)",
            }}
          >
            <motion.div
              initial={{ scale: 0, rotate: -30 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 16, delay: 0.15 }}
            >
              <Check size={44} className="text-white" strokeWidth={3.5} />
            </motion.div>
            <span className="absolute inset-0 rounded-full ring-4 ring-emerald-300/30 animate-ping" style={{ animationDuration: "2.4s" }} />
          </div>

          {/* Back - dumbbell */}
          <div
            className="absolute inset-0 rounded-full flex items-center justify-center"
            style={{
              backfaceVisibility: "hidden",
              transform: "rotateY(180deg)",
              background: "linear-gradient(135deg, var(--accent), var(--accent-2))",
              boxShadow: "0 12px 40px -8px var(--accent-glow), inset 0 1px 0 rgba(255,255,255,0.4)",
            }}
          >
            <Dumbbell size={42} className="text-white" strokeWidth={2.4} />
          </div>
        </motion.div>
      </div>

      <AnimatePresence>
        {done && (
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-7 max-w-sm text-base sm:text-lg leading-relaxed text-[var(--text)] font-medium px-4"
            data-testid="success-text"
          >
            {message}
          </motion.p>
        )}
      </AnimatePresence>
      {!done && (
        <p className="mt-7 max-w-sm text-base sm:text-lg leading-relaxed text-[var(--text-dim)] px-4">
          {message}
        </p>
      )}
    </div>
  );
};

export default SuccessAnimation;
